import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { List, Typography, Row, Col, Button } from 'antd';
import { InfiniteScroll } from 'antd-mobile';
import { PlusOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Rest from 'tools/rest';

const { Text } = Typography;

const MobileAgreementRequests = ({
  agrmId,
  agreement,
  agreementsRouteId,
  initialRequests,
  initialRequestsTotal,
}) => {
  const history = useHistory();
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    per: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const seededFromShowRef = useRef(false);

  useEffect(() => {
    seededFromShowRef.current = false;
    setMeta((m) => ({ ...m, page: 1, total: 0 }));
    setRequests([]);
  }, [agrmId]);

  useEffect(() => {
    if (!agrmId) return;
    if (!Array.isArray(initialRequests) || initialRequests.length === 0) return;
    setRequests(initialRequests);
    setMeta((m) => ({
      ...m,
      page: 1,
      total:
        typeof initialRequestsTotal === 'number'
          ? initialRequestsTotal
          : initialRequests.length,
    }));
    seededFromShowRef.current = true;
  }, [agrmId, initialRequests, initialRequestsTotal]);

  useEffect(() => {
    if (!agrmId) return;
    if (meta.page === 1 && seededFromShowRef.current) return;

    setLoading(true);
    const page = meta.page;
    const params = {
      page,
      per: meta.per,
      search: { agrm_id: agrmId },
    };
    Rest.get('/api/v1/requests.json', { params })
      .then((response) => {
        const { requests: rows, meta: m } = response.data;
        setRequests((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setMeta((prev) => ({ ...prev, ...m, total: m.total }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [agrmId, meta.page]);

  const loadMore = () => {
    if (loading || requests.length >= meta.total) return;
    setMeta((m) => ({ ...m, page: m.page + 1 }));
  };

  const goCreate = () => {
    history.push({
      pathname: '/m/requests/create',
      state: {
        agreementResource: {
          resource_id: agrmId,
          resource_type: 'LbAgreement',
          resource: {
            identifier: agreement?.number,
            address: agreement?.address,
            name: agreement?.name,
            phone: agreement?.phone,
            uid: agreement?.uid,
          },
        },
        returnToAgreement:
          agreementsRouteId != null
            ? { agreementsId: agreementsRouteId, activeTab: 4 }
            : undefined,
      },
    });
  };

  if (!agrmId) {
    return (
      <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>Нет номера договора</div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 200 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={goCreate}
        style={{ marginBottom: 12, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        block
      >
        Создать задачу
      </Button>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Общее кол-во: {meta.total} шт.
      </Text>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <List
          loading={loading && meta.page === 1 && requests.length === 0}
          dataSource={requests}
          locale={{ emptyText: 'Нет задач по этому договору' }}
          renderItem={(req) => (
            <Link
              to={{
                pathname: `/m/requests/${req.id}`,
                ...(agreementsRouteId != null
                  ? {
                      state: {
                        returnToAgreement: {
                          agreementsId: agreementsRouteId,
                          activeTab: 4,
                        },
                      },
                    }
                  : {}),
              }}
            >
              <List.Item
                style={{
                  marginBottom: 8,
                  padding: '10px 12px',
                  border: '1px solid #e8e8e8',
                  borderRadius: 8,
                  backgroundColor: '#FFFFFF',
                  alignItems: 'flex-start',
                }}
              >
                <Row style={{ width: '100%' }} gutter={[0, 4]}>
                  <Col span={24}>
                    <Text style={{ color: '#262626', fontSize: 15, fontWeight: 600 }}>
                      Задача № {req.id}
                      {req.has_files && (
                        <PaperClipOutlined style={{ marginLeft: 6, color: '#8c8c8c', fontSize: 14 }} />
                      )}
                    </Text>
                  </Col>
                  {req.request_type?.name && (
                    <Col span={24}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Тип: {req.request_type.name}
                      </Text>
                    </Col>
                  )}
                  <Col span={24}>
                    <Text style={{ fontSize: 13, color: '#262626', display: 'block' }}>
                      {req.description
                        ? req.description.length > 160
                          ? `${req.description.slice(0, 160)}…`
                          : req.description
                        : '—'}
                    </Text>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Начало:{' '}
                      {req.plan_started_at
                        ? dayjs(req.plan_started_at).format('DD.MM.YYYY HH:mm')
                        : '—'}
                    </Text>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Исполнитель: {req.executor_user?.name || '—'}
                    </Text>
                  </Col>
                </Row>
              </List.Item>
            </Link>
          )}
        />
        <InfiniteScroll loadMore={loadMore} hasMore={requests.length < meta.total}>
          <span />
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default MobileAgreementRequests;
